import { useState } from "react";
import type { FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da1cqo9st185ek8asoog";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [formMsg, setFormMsg] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormMsg("提交成功，我们会尽快与你联系。");
      form.reset();
      return;
    }

    formData.delete("website_alt");

    setStatus("loading");
    setFormError("");
    setFormMsg("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        setFormMsg("提交成功，我们会尽快与你联系。");
        form.reset();
      } else {
        setStatus("error");
        setFormError(typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查连接后重试。");
    }
  }

  return (
    <section id="contact" className="relative w-full bg-background-100 py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600">
              <i className="ri-message-3-line"></i>
              联系我们
            </span>
            <h2 className="font-display text-2xl md:text-4xl text-foreground-950 mt-3 leading-snug">
              有下不动的资源？
              <br className="hidden md:block" />
              告诉我们你的需求
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              遇到具体的下载问题、想补充新的方式，或对教程有任何建议，都欢迎留言。
              我们会认真阅读每一条反馈，并尽量给出针对性的解决方法。
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">邮箱</p>
                  <p className="text-sm font-medium text-foreground-900">hello@mancang.example.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-700">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">回复时间</p>
                  <p className="text-sm font-medium text-foreground-900">工作日 24 小时内回复</p>
                </div>
              </div>
            </div>
          </div>

          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="rounded-3xl bg-background-50 border border-background-200 p-6 md:p-8 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  称呼
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="怎么称呼你"
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  邮箱
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                主题
              </label>
              <input
                id="subject"
                name="subject"
                type="text"
                placeholder="例如：离线下载一直失败"
                className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                需求描述
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="把遇到的问题或需求说清楚，我们好对症下药…"
                className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              ></textarea>
              <p className="mt-1 text-xs text-foreground-400">最多 500 字</p>
            </div>

            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="frm__aux"
            />

            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 text-background-50 font-semibold px-6 py-3 hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {status === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  提交中…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  提交需求
                </>
              )}
            </button>

            {status === "success" && (
              <p className="flex items-center gap-2 text-sm text-secondary-600 bg-secondary-100/60 rounded-lg px-4 py-3">
                <i className="ri-checkbox-circle-line"></i>
                {formMsg}
              </p>
            )}
            {status === "error" && formError && (
              <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-100/60 rounded-lg px-4 py-3">
                <i className="ri-error-warning-line mt-0.5"></i>
                {formError}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}